/* ============================================================
   Papermill theme — global interactions
   Vanilla JS, no dependencies. Progressive enhancement.
   ============================================================ */
(function () {
  'use strict';

  const on = (el, evt, fn) => el && el.addEventListener(evt, fn);
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from((ctx || document).querySelectorAll(sel));

  const formatMoney = (cents) => {
    const group = (decimals, thousands, decimal) => {
      const parts = (cents / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    const format = window.moneyFormat || '${{ amount }}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(0, ',', '.');
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        default: return group(2, ',', '.');
      }
    });
  };

  /* ---------------- Overlay drawers (menu, search, cart) ---------------- */
  function openDrawer(el) {
    if (!el) return;
    el.classList.add('active');
    document.body.style.overflow = 'hidden';
    const focusable = el.querySelector('[autofocus], input, button');
    if (focusable) setTimeout(() => focusable.focus(), 60);
  }
  function closeDrawer(el) {
    if (!el) return;
    el.classList.remove('active');
    document.body.style.overflow = '';
  }
  function closeAllDrawers() {
    qsa('.cart-drawer.active, .menu-drawer.active, .search-modal.active, .facets.active').forEach(closeDrawer);
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(qs(opener.getAttribute('data-drawer-open')));
    }
    if (e.target.closest('[data-drawer-close]') ||
        e.target.classList.contains('cart-drawer__overlay') ||
        e.target.classList.contains('menu-drawer__overlay') ||
        e.target.classList.contains('search-modal__overlay')) {
      const parent = e.target.closest('.cart-drawer, .menu-drawer, .search-modal, .facets');
      closeDrawer(parent || qs('.cart-drawer.active'));
      if (!parent) closeAllDrawers();
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAllDrawers(); });

  /* ---------------- Quantity inputs ---------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.quantity__button');
    if (!btn) return;
    const wrap = btn.closest('.quantity');
    const input = wrap.querySelector('.quantity__input');
    let val = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    if (btn.dataset.action === 'increase') val += 1;
    else val = Math.max(min, val - 1);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------- Cart API ---------------- */
  const Cart = {
    async add(formData) {
      const res = await fetch((window.routes?.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      if (!res.ok) throw new Error('add failed');
      await this.refresh();
      return res.json();
    },
    async change(key, quantity) {
      const res = await fetch((window.routes?.cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      await this.refresh(cart);
      return cart;
    },
    async get() {
      const res = await fetch((window.routes?.cart_url || '/cart') + '.js');
      return res.json();
    },
    async refresh(cart) {
      if (!cart) cart = await this.get();
      // Update bubble counts
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      // Re-render cart drawer via Section Rendering API
      try {
        const html = await fetch(`${window.shopUrl || ''}/?section_id=cart-drawer`).then(r => r.text());
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('.cart-drawer__body');
        const target = qs('.cart-drawer__body');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      } catch (e) { /* section rendering not available in preview */ }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    }
  };
  window.PapermillCart = Cart;

  /* ---------------- Add-to-cart forms ---------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form], form.quick-add-form');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const original = btn ? btn.innerHTML : '';
    if (btn) { btn.classList.add('loading'); btn.setAttribute('aria-busy', 'true'); btn.disabled = true; btn.innerHTML = '…'; }
    try {
      await Cart.add(new FormData(form));
      openDrawer(qs('.cart-drawer'));
    } catch (err) {
      console.error(err);
      window.location.href = '/cart';
    } finally {
      if (btn) { btn.classList.remove('loading'); btn.removeAttribute('aria-busy'); btn.disabled = false; btn.innerHTML = original; }
    }
  });

  /* ---------------- Cart drawer + cart page quantity/remove ---------------- */
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-quantity]');
    if (!input) return;
    const key = input.getAttribute('data-cart-quantity');
    const row = input.closest('[data-cart-item]');
    let qty = parseInt(input.value, 10);
    // A blanked/garbage field falls back to 1 — only an explicit 0 removes the item.
    if (!Number.isFinite(qty) || qty < 0) { qty = 1; input.value = qty; }
    if (row) row.classList.add('loading');
    await Cart.change(key, qty);
    if (window.location.pathname.includes('/cart')) window.location.reload();
  });
  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    const key = remove.getAttribute('data-cart-remove');
    const row = remove.closest('[data-cart-item]');
    if (row) row.classList.add('loading');
    await Cart.change(key, 0);
    if (window.location.pathname.includes('/cart')) window.location.reload();
  });

  /* ---------------- Variant selection (product page) ---------------- */
  class VariantSelector {
    constructor(root) {
      this.root = root;
      this.productData = JSON.parse(qs('[data-product-json]', root)?.textContent || '{}');
      this.priceEl = qs('[data-product-price]', root);
      this.form = qs('form[data-product-form]', root);
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.addBtn = this.form ? this.form.querySelector('[data-add-button]') : null;
      root.addEventListener('change', (e) => {
        if (e.target.closest('[data-variant-option]')) this.update();
      });
    }
    getSelectedOptions() {
      return qsa('[data-variant-option]', this.root).map((group) => {
        const checked = group.querySelector('input:checked');
        if (checked) return checked.value;
        if (group.tagName === 'SELECT') return group.value;
        const sel = group.querySelector('select');
        return sel ? sel.value : null;
      });
    }
    update() {
      // Keep the visual "selected" state in sync with the actually-checked radios,
      // so the default pill's server-rendered .is-selected class doesn't stick.
      qsa('.variant-pill', this.root).forEach((pill) => {
        const input = pill.querySelector('input');
        pill.classList.toggle('is-selected', !!(input && input.checked));
      });
      const opts = this.getSelectedOptions();
      const variants = this.productData.variants || [];
      const match = variants.find((v) => opts.every((o, i) => v.options[i] === o));
      if (!match) { this.setUnavailable(); return; }
      if (this.idInput) this.idInput.value = match.id;
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
      if (this.priceEl) {
        let html = `<span class="price__regular">${formatMoney(match.price)}</span>`;
        if (match.compare_at_price > match.price) {
          html = `<span class="price__sale">${formatMoney(match.price)}</span><s class="price__compare">${formatMoney(match.compare_at_price)}</s>`;
        }
        this.priceEl.innerHTML = html;
      }
      if (this.addBtn) {
        if (match.available) { this.addBtn.disabled = false; this.addBtn.querySelector('span').textContent = window.variantStrings?.addToCart || 'Add to cart'; }
        else { this.addBtn.disabled = true; this.addBtn.querySelector('span').textContent = window.variantStrings?.soldOut || 'Sold out'; }
      }
      if (window.PapermillVolume) {
        window.PapermillVolume.setBasePrice(match.price);
        window.PapermillVolume.setVariant(match.id);
        const q = qs('.quantity__input', this.root);
        window.PapermillVolume.update(q ? q.value : 1);
      }
    }
    setUnavailable() {
      if (this.addBtn) { this.addBtn.disabled = true; this.addBtn.querySelector('span').textContent = window.variantStrings?.unavailable || 'Unavailable'; }
    }
  }
  qsa('[data-variant-selector]').forEach((el) => new VariantSelector(el));

  /* ---------------- Slideshow ---------------- */
  qsa('[data-slideshow]').forEach((root) => {
    const slides = qsa('.slideshow__slide', root);
    if (slides.length < 2) return;
    const dots = qsa('.slideshow__dot', root);
    let index = 0;
    let timer;
    const interval = parseInt(root.dataset.interval, 10) || 6000;
    const go = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
      dots.forEach((d, n) => d.classList.toggle('is-active', n === index));
    };
    const next = () => go(index + 1);
    const prev = () => go(index - 1);
    const play = () => { if (root.dataset.autoplay === 'true') timer = setInterval(next, interval); };
    const stop = () => clearInterval(timer);
    on(qs('[data-slide-next]', root), 'click', () => { next(); stop(); play(); });
    on(qs('[data-slide-prev]', root), 'click', () => { prev(); stop(); play(); });
    dots.forEach((d, n) => on(d, 'click', () => { go(n); stop(); play(); }));
    on(root, 'mouseenter', stop);
    on(root, 'mouseleave', play);
    go(0); play();
  });

  /* ---------------- Predictive search ---------------- */
  const searchInput = qs('[data-predictive-search-input]');
  if (searchInput) {
    const resultsEl = qs('[data-predictive-search-results]');
    let debounce;
    on(searchInput, 'input', () => {
      clearTimeout(debounce);
      const term = searchInput.value.trim();
      if (term.length < 2) { if (resultsEl) resultsEl.innerHTML = ''; return; }
      debounce = setTimeout(async () => {
        try {
          const url = `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const html = await fetch(url).then(r => r.text());
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('[data-predictive-search-results]');
          if (fresh && resultsEl) resultsEl.innerHTML = fresh.innerHTML;
        } catch (e) { /* noop */ }
      }, 250);
    });
  }

  /* ---------------- Facets: auto-submit on change ---------------- */
  const facetForm = qs('[data-facet-form]');
  if (facetForm) {
    facetForm.addEventListener('change', () => {
      if (facetForm.dataset.autoSubmit !== 'false') facetForm.submit();
    });
  }

  /* ---------------- Sticky header shrink on scroll ---------------- */
  const header = qs('.header-wrapper');
  if (header) {
    let last = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 40);
      last = y;
    }, { passive: true });
  }

  /* ---------------- Collection grid / list view toggle ---------------- */
  (function () {
    const grid = qs('[data-product-grid]');
    const toggle = qs('[data-view-toggle]');
    if (!grid) return;
    const KEY = 'papermill:collection-view';
    const apply = (view) => {
      const isList = view === 'list';
      grid.classList.toggle('product-grid--list', isList);
      if (toggle) qsa('.view-toggle__btn', toggle).forEach((b) => {
        const active = b.dataset.view === view;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };
    let saved = null;
    // Only honor the shopper's saved preference when the toggle is actually shown,
    // otherwise a stale localStorage value overrides the merchant's setting.
    if (toggle) { try { saved = localStorage.getItem(KEY); } catch (e) {} }
    apply(saved || grid.dataset.defaultView || 'grid');
    if (toggle) toggle.addEventListener('click', (e) => {
      const btn = e.target.closest('.view-toggle__btn');
      if (!btn) return;
      apply(btn.dataset.view);
      try { localStorage.setItem(KEY, btn.dataset.view); } catch (e) {}
    });
  })();

  /* ---------------- Volume / bulk pricing (live per-unit price) ---------------- */
  const VolumePricing = (function () {
    const root = qs('[data-volume-pricing]');
    if (!root) return null;
    const live = qs('[data-volume-live]', root);
    const mode = root.dataset.mode;
    const tables = qsa('[data-volume-table]', root);
    let activeTable = tables.find((t) => !t.hidden) || tables[0] || null;
    let basePrice = parseInt(root.dataset.basePrice, 10) || 0;
    let lastQty = 1;

    const rows = () => (activeTable ? qsa('[data-volume-row]', activeTable) : []);
    const tiers = () => rows().map((row) => ({
      row,
      min: parseInt(row.dataset.min, 10) || 1,
      discount: row.dataset.discount != null && row.dataset.discount !== '' ? parseInt(row.dataset.discount, 10) : null,
      priceCents: parseInt(row.dataset.priceCents, 10) || 0
    }));

    function recalcPrices() {
      // Percent tiers are relative to the variant price, so recompute when the variant changes.
      // Absolute (native price-break) tables are per-variant and swapped by setVariant instead.
      if (mode !== 'percent') return;
      tiers().forEach((t) => {
        const cents = t.discount == null ? basePrice : Math.round(basePrice * (100 - t.discount) / 100);
        t.row.dataset.priceCents = cents;
        const cell = t.row.querySelector('[data-volume-unit]');
        if (cell) cell.textContent = formatMoney(cents);
      });
    }

    function activeTier(qty) {
      let chosen = null;
      tiers().forEach((t) => { if (qty >= t.min && (!chosen || t.min > chosen.min)) chosen = t; });
      return chosen;
    }

    function update(qty) {
      qty = parseInt(qty, 10) || 1;
      lastQty = qty;
      const tier = activeTier(qty);
      rows().forEach((r) => r.classList.remove('is-active'));
      if (!tier) { if (live) live.textContent = ''; return; }
      tier.row.classList.add('is-active');
      if (live) {
        live.textContent = tier.min > 1 && window.volumeStrings
          ? (window.volumeStrings.yourPrice || '').replace('__price__', formatMoney(tier.priceCents))
          : '';
      }
    }

    function setBasePrice(cents) {
      basePrice = parseInt(cents, 10) || basePrice;
      recalcPrices();
    }

    function setVariant(id) {
      if (mode !== 'absolute') return;
      activeTable = null;
      tables.forEach((t) => {
        const match = t.dataset.variantId === String(id);
        t.hidden = !match;
        if (match) activeTable = t;
      });
      // Hide the whole widget when the selected variant has no price breaks.
      root.hidden = !activeTable;
      update(lastQty);
    }

    return { update, setBasePrice, setVariant };
  })();
  window.PapermillVolume = VolumePricing;

  if (VolumePricing) {
    const qtyInput = qs('[data-variant-selector] .quantity__input') || qs('.product__form .quantity__input');
    if (qtyInput) {
      const push = () => VolumePricing.update(qtyInput.value);
      on(qtyInput, 'change', push);
      on(qtyInput, 'input', push);
      push();
    }
  }

  /* ---------------- Share button ---------------- */
  document.addEventListener('click', async (e) => {
    const share = e.target.closest('[data-share-button]');
    if (!share) return;
    const url = share.dataset.shareUrl || window.location.href;
    if (navigator.share) {
      try { await navigator.share({ url, title: document.title }); } catch (err) {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        const msg = share.querySelector('[data-share-message]');
        if (msg) { msg.hidden = false; setTimeout(() => { msg.hidden = true; }, 2000); }
      } catch (err) {}
    }
  });
})();
